/** @jsx jsx */
import { useContext } from 'react';
import { css, jsx } from '@compiled/react';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import Page16Icon from '@atlaskit/icon-object/glyph/page/16';
import type { CreatePayload } from '@atlaskit/link-create';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useTenantHasConfluence } from '@atlassian/jira-business-confluence-pages';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { ACTION_SUBJECTS, ACTIONS } from '../../../common/constants';
import { CONTENT_TYPE, type CreateContentModalCoreProps } from '../../../common/types/types';
import { ContentMenuContext } from '../../../controllers/create-content-menu-context';
import type { SpaceInfo } from '../../../controllers/types';
import { useConfluenceSite } from '../../../controllers/use-confluence-site';
import { useCreateContentClickBehavior } from '../../../controllers/use-create-content-click-behavior';
import { useEmbeddedConfluenceDefaultSpace } from '../../../controllers/use-embedded-confluence-default-space';
import { useEmbeddedPageTracking } from '../../../controllers/use-embedded-page-tracking';
import { createConfluenceDraft } from '../../../services/confluence-rest-api';
import type { CreateConfluenceDraftV2Response } from '../../../services/confluence-rest-api/types';
import {
	convertUrlSpaceIdToSpaceKey,
	createLinkCreatePayload,
	getDraftShareId,
	getURLFromConfluencePageResponse,
} from '../../../utils';
import messages from './messages';

export const CreatePageMenuItem = ({
	onCancel,
	onCloseComplete,
	onCreate,
	onFailure,
	onOpenComplete,
	onCreateBehavior,
	isQuickActionsListView,
}: CreateContentModalCoreProps) => {
	const { formatMessage } = useIntl();
	const {
		accessStatus,
		setIsCreateContentModalOpen,
		setCreateContentRuntimeProps,
		setActivePageUrl,
		setIsEpModalOpen,
		embeddedConfluenceSource,
		userHasConfluenceAccess,
		setIsPublishModalEnabled,
		setIsEpModalLoading,
	} = useContext(ContentMenuContext);
	const fireAnalyticsEvent = useEmbeddedPageTracking();
	const getCreateContentClickBehavior = useCreateContentClickBehavior();
	const tenantHasConfluence = useTenantHasConfluence();
	const cloudId = useCloudId();
	const { getEmbeddedConfluenceDefaultSpace } = useEmbeddedConfluenceDefaultSpace();
	const { getConfluenceSite } = useConfluenceSite();
	const actionSubjects = ACTION_SUBJECTS[CONTENT_TYPE.PAGE];

	const createConfluencePage = async () => {
		const openCreateContentModal = () => {
			setIsCreateContentModalOpen(true);
			setCreateContentRuntimeProps({
				isOpen: true,
				isQuickActionsListView,
				contentType: CONTENT_TYPE.PAGE,
				onCancel,
				onCloseComplete,
				onCreate: (payload: CreatePayload): void => {
					onCreate && onCreate(payload);
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const links = payload.data?.links as Record<string, string>;
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const spaceKey = payload.data?.spaceKey as string;
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const spaceId = payload.data?.spaceId as string;

					const linkableUrl = convertUrlSpaceIdToSpaceKey({
						url: payload.url,
						spaceId,
						spaceKey,
					});

					setActivePageUrl(`${linkableUrl}?draftShareId=${getDraftShareId(links?.editui)}`);
					setIsEpModalOpen(true);
				},
				onFailure,
				onOpenComplete,
				onCreateBehavior,
			});
		};

		const ldExperimentEnabled = ff('platform_embed-conf_jira-issue_co-use');
		if (
			!ldExperimentEnabled ||
			!expValEquals('platform_embed_conf_jira-issue_co-use_exp', 'cohort', 'experiment')
		) {
			openCreateContentModal();
			return;
		}

		setIsEpModalLoading(true);
		const defaultSpace: SpaceInfo = await getEmbeddedConfluenceDefaultSpace();
		const { spaceId, spaceKey } = defaultSpace;

		const site = await getConfluenceSite();

		if (!spaceKey || !spaceId) {
			openCreateContentModal();
			setIsEpModalLoading(false);
			return;
		}

		const fetchResponse = await createConfluenceDraft(cloudId, spaceId);
		if (!fetchResponse.success) {
			fireErrorAnalytics({
				meta: {
					id: actionSubjects.CREATE,
					teamName: 'confluence-better-together',
				},
				attributes: { embeddedConfluenceSource },
				error: new Error(`Failed to create Confluence content: ${fetchResponse.error}`),
			});
			// Don't want to run onFailure here because we are defaulting back to using the Create Content Modal
			openCreateContentModal();
			setIsEpModalLoading(false);
			return;
		}

		const createResponse: CreateConfluenceDraftV2Response = fetchResponse.result;

		const url = getURLFromConfluencePageResponse({
			id: createResponse.id,
			spaceKey,
			hostname: site,
		});

		// TODO: If experiment is successful, de-couple this from CreatePayload and Link-Create
		const payload = createLinkCreatePayload(createResponse, url, defaultSpace, cloudId);
		onCreate && onCreate(payload);

		fireAnalyticsEvent({
			action: ACTIONS.SUCCESS,
			eventName: actionSubjects.CREATE,
			embeddedConfluenceSource,
		});

		setIsEpModalLoading(false);

		setActivePageUrl(`${url}?draftShareId=${getDraftShareId(createResponse._links?.editui)}`);
		setIsPublishModalEnabled(true);
		setIsEpModalOpen(true);
	};

	const onOpen = () => {
		fireAnalyticsEvent({
			action: 'clicked',
			eventName: 'createPageMenuItem',
			embeddedConfluenceSource,
			userHasConfluenceAccess,
			tenantHasConfluence,
			crossJoinStatus: accessStatus,
		});
		return getCreateContentClickBehavior({
			accessStatus,
			createContent: createConfluencePage,
			tenantHasConfluence,
		})();
	};

	if (ff('issue_view_quick_actions_revamp_l8mbx') && isQuickActionsListView) {
		return (
			<DropdownItem
				testId="issue-create-confluence-content.ui.create-confluence-content-menu.create-page-menu-item.create-page-menu-item"
				onClick={onOpen}
				elemBefore={
					<Box paddingInlineStart="space.050">
						<Page16Icon label="" />
					</Box>
				}
			>
				<span css={messageStyles}>
					{formatMessage(messages.createConfluencePageQuickActionList)}
				</span>
			</DropdownItem>
		);
	}

	return (
		<DropdownItem
			testId="issue-create-confluence-content.ui.create-confluence-content-menu.create-page-menu-item.create-page-menu-item"
			onClick={onOpen}
			elemBefore={<Page16Icon label="" />}
		>
			{formatMessage(messages.createConfluencePageItem)}
		</DropdownItem>
	);
};

const messageStyles = css({
	paddingLeft: token('space.050'),
});
